import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FOUND_CONFIG, GREEN, LEGAL_NAV } from "./foundConfig";

export const H2 = ({ children, id }) => (
  <h2
    id={id}
    className="font-syne font-bold text-white text-2xl md:text-3xl tracking-tight mt-14 mb-4 scroll-mt-24"
  >
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="font-syne font-semibold text-white text-lg md:text-xl mt-8 mb-3">{children}</h3>
);

export const P = ({ children }) => (
  <p className="font-manrope text-[15px] md:text-base text-stone-300 leading-[1.75] mb-4">{children}</p>
);

export const List = ({ items }) => (
  <ul className="mb-5 space-y-2">
    {(items || []).map((t, i) => (
      <li key={i} className="flex items-start gap-3 font-manrope text-[15px] text-stone-300 leading-relaxed">
        <span className="mt-2 w-1.5 h-1.5 shrink-0" style={{ backgroundColor: GREEN }} />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export const Note = ({ children }) => (
  <div
    className="my-6 border-l-2 pl-4 py-2 font-manrope text-sm text-stone-400 leading-relaxed"
    style={{ borderColor: GREEN }}
  >
    {children}
  </div>
);

export const LegalLayout = ({ title, description, children }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = `${title} · FOUND — Dream Big Entertainment`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", description || `${title} for FOUND, operated by ${FOUND_CONFIG.company}.`);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", FOUND_CONFIG.siteUrl + pathname);
    return () => {
      document.title = "FOUND — Discover Music Before The World Does";
    };
  }, [title, description, pathname]);

  return (
    <div className="min-h-screen bg-[#070707] text-stone-200">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#070707]/90 backdrop-blur border-b border-stone-800/60">
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="legal-home-link">
            <ArrowLeft size={16} className="text-stone-400 group-hover:text-white transition-colors" />
            <img src="/found/logo.png" alt="FOUND logo" className="h-7 w-7 rounded-full" />
            <span className="font-syne font-extrabold tracking-[0.14em] text-sm text-white">
              FOUND
            </span>
          </Link>
          <span className="font-manrope text-[11px] tracking-[0.22em]" style={{ color: GREEN }}>
            FOUND<span className="text-stone-500"> · LEGAL &amp; SUPPORT</span>
          </span>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <p className="font-manrope text-xs tracking-[0.3em] mb-4" style={{ color: GREEN }}>
          FOUND — MUSIC DISCOVERY
        </p>
        <h1 className="font-syne font-extrabold text-white text-4xl md:text-5xl tracking-tight leading-[1.05]">
          {title}
        </h1>
        <p className="font-manrope text-sm text-stone-500 mt-4">
          Operated by {FOUND_CONFIG.company} · {FOUND_CONFIG.location}
        </p>

        <div className="mt-10">{children}</div>
      </main>

      {/* Footer nav */}
      <footer className="border-t border-stone-800/60">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
          <p className="font-manrope text-[11px] tracking-[0.25em] text-stone-500 mb-5">FOUND — LEGAL &amp; SUPPORT</p>
          <nav className="flex flex-wrap gap-x-6 gap-y-3">
            {LEGAL_NAV.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-manrope text-sm text-stone-400 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <p className="font-manrope text-xs text-stone-600 mt-8">
            © 2026 {FOUND_CONFIG.company}. All rights reserved. ·{" "}
            <Link to="/" className="hover:text-stone-400">Back to Dream Big Entertainment</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};
